/**
 * Firestore paths and document shapes for storytelling pages: admin-only
 * pages that tell the story of a course (for example, how student
 * summaries work in CIPX), shown at `/:courseId/storytelling`.
 *
 * ```
 * storytelling/{courseId}/pages/{pageId}                       -- metadata
 * storytelling/{courseId}/pages/{pageId}/versions/{versionId}  -- the HTML
 * ```
 *
 * The HTML sits in a subcollection so the index can list pages without
 * downloading every page body. A page's HTML can embed anonymized student
 * data, which is why it lives in Firestore and never in the repo. Only
 * `publishStorytellingPage.ts` (admin SDK) writes here; the rules allow
 * reads for course admins and no client writes.
 *
 * Tracking: https://github.com/CodeinPlace/cip/issues/6440
 */
import {z} from 'zod';

/** Top-level collection name. */
export const STORYTELLING_COLLECTION = 'storytelling';

/** Subcollection of page metadata docs under each course. */
export const STORYTELLING_PAGES_SUBCOLLECTION = 'pages';

/** Subcollection of HTML versions under each page. */
export const STORYTELLING_VERSIONS_SUBCOLLECTION = 'versions';

/**
 * Allowed page and version ids: lowercase words joined by hyphens, as
 * they appear in the URL (for example `student-summaries`).
 */
export const STORYTELLING_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Builds the collection path for a course's storytelling pages.
 *
 * @param courseId - Course the pages belong to.
 * @returns For example `storytelling/cipx/pages`.
 */
export function getStorytellingPagesCollectionPath(courseId: string): string {
  return `${STORYTELLING_COLLECTION}/${courseId}/${STORYTELLING_PAGES_SUBCOLLECTION}`;
}

/**
 * Builds the path of one page's metadata doc.
 *
 * @param courseId - Course the page belongs to.
 * @param pageId - Page id from the URL.
 * @returns For example `storytelling/cipx/pages/student-summaries`.
 */
export function getStorytellingPageDocPath(
  courseId: string,
  pageId: string,
): string {
  return `${getStorytellingPagesCollectionPath(courseId)}/${pageId}`;
}

/**
 * Builds the path of one stored HTML version of a page.
 *
 * @param courseId - Course the page belongs to.
 * @param pageId - Page id from the URL.
 * @param versionId - Version id, as stored in `currentVersion`.
 * @returns For example
 *   `storytelling/cipx/pages/student-summaries/versions/2026-11-02-1`.
 */
export function getStorytellingVersionDocPath(
  courseId: string,
  pageId: string,
  versionId: string,
): string {
  return `${getStorytellingPageDocPath(courseId, pageId)}/${STORYTELLING_VERSIONS_SUBCOLLECTION}/${versionId}`;
}

/**
 * Metadata doc at `storytelling/{courseId}/pages/{pageId}`. Timestamps
 * are epoch milliseconds so the schema is the same for the admin and
 * client SDKs.
 */
export const StorytellingPageSchema = z.object({
  title: z.string().min(1),
  description: z.string().default(''),
  currentVersion: z.string().regex(STORYTELLING_ID_PATTERN),
  updatedAtMs: z.number(),
  updatedBy: z.string(),
  sizeBytes: z.number(),
});

/** Parsed {@link StorytellingPageSchema}. */
export type StorytellingPage = z.infer<typeof StorytellingPageSchema>;

/** One stored version at `.../versions/{versionId}`. */
export const StorytellingVersionSchema = z.object({
  html: z.string().min(1),
  createdAtMs: z.number(),
  createdBy: z.string(),
  note: z.string().default(''),
});

/** Parsed {@link StorytellingVersionSchema}. */
export type StorytellingVersion = z.infer<typeof StorytellingVersionSchema>;

/**
 * Courses whose storytelling pages the index lists, in display order.
 * Pages for the URL's own course link as `storytelling/{pageId}`; pages
 * for another course link as `storytelling/{courseId}/{pageId}`.
 */
export const STORYTELLING_COURSES: ReadonlyArray<{
  courseId: string;
  label: string;
}> = [
  {courseId: 'cipx', label: 'CIPX'},
  {courseId: 'pai1', label: 'PAI'},
];
